import {
  ChecklistItemStatusType,
  FormType,
} from "@kolibrisoftware/customerportal-api";
import { useMedia, useTranslation } from "@kolibrisoftware/hooks-util";
import { FormLoadingStatus } from "enums/form-loading-status";
import { IntersectionObserverInjector } from "injectors/intersection-observer/intersection-observer";
import { PresenceProvider } from "injectors/presence";
import { BackActionLayout } from "layouts/back-action-layout/back-action-layout";
import MobileBackActionLayout from "layouts/back-action-layout/mobile-back-action-layout";
import { FC, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { ErrorPage } from "shared/components/error-page/error-page";
import { Presence } from "shared/components/presence";
import { LoadingScreen } from "shared/ui/loading-screen/loading-screen";
import { selectFlatChecklistItems } from "store/dossier/selectors";
import { useDispatch, useSelector } from "store/helpers";
import { actions as questionnaireActions } from "store/questionnaire";
import { selectQuestionnaireForm } from "store/questionnaire/selectors";
import ReviewChecklistItem from "../../shared/components/review-checklist-item-button/review-checklist-item-button";
import MobileSidebar from "./clusters/mobile-sidebar";
import InitialDataLayer from "./initial-data-layer";
import RootScreen from "./screens/root-screen";

type Props = {};

const QuestionnaireModule: FC<Props> = props => {
  const { savingStatus, formEditableStatus, formLoadingStatus } = useSelector(
    state => state.questionnaire
  );
  const allChecklistItems = useSelector(selectFlatChecklistItems);
  const renewHintRef = useRef(false);
  const dispatch = useDispatch();
  const { dossierId } = useParams();
  const { checklistItemId } = useSelector(
    state => state.questionnaire.formStructure
  );

  const attachedChecklistItem = useMemo(
    () => allChecklistItems?.find(item => item.id === checklistItemId),
    [allChecklistItems, checklistItemId]
  );

  const isPersonalia =
    useSelector(selectQuestionnaireForm).type === FormType.PersonalDetails;

  const isScreenUnder1150 = useMedia("(max-width:1150px)");

  const t = useTranslation();

  const translations = useMemo(
    () => ({
      pageTile: isPersonalia
        ? t("personalia.pageTitlePlaceholder")
        : t("questionnaire.pageTitlePlaceholder"),
      backActionLabel: t("questionnaire.backActionLabel"),
    }),
    [t, isPersonalia]
  );

  const showEditActions = useMemo(
    () => formEditableStatus === ChecklistItemStatusType.Open,
    [formEditableStatus]
  );

  useEffect(() => {
    if (attachedChecklistItem) {
      dispatch(
        questionnaireActions.setFormEditableStatus(
          attachedChecklistItem?.status as ChecklistItemStatusType
        )
      );
      renewHintRef.current = !renewHintRef.current;
    }
  }, [attachedChecklistItem, dispatch]);

  return (
    <IntersectionObserverInjector>
      <InitialDataLayer>
        {formLoadingStatus === FormLoadingStatus.Loading && (
          <LoadingScreen useCase="document" showSide={true} />
        )}
        {formLoadingStatus === FormLoadingStatus.Failed && (
          <ErrorPage useCase="FormError" />
        )}
        {formLoadingStatus === FormLoadingStatus.Success && (
          <PresenceProvider formType={FormType.Questionnaire}>
            <>
              {isScreenUnder1150 ? (
                <MobileBackActionLayout
                  centerTitle={translations.pageTile}
                  optionalSideBar={<MobileSidebar />}
                  backActionLabel={translations.backActionLabel}
                  savingStatus={showEditActions ? savingStatus : undefined}
                  presence={<Presence />}
                  reviewButton={
                    showEditActions ? (
                      <ReviewChecklistItem
                        dossierId={dossierId || ""}
                        checklistItemId={checklistItemId || ""}
                        disabled={
                          formEditableStatus !== ChecklistItemStatusType.Open
                        }
                      />
                    ) : undefined
                  }
                >
                  <RootScreen renewHint={renewHintRef.current} />
                </MobileBackActionLayout>
              ) : (
                <BackActionLayout
                  centerTitle={translations.pageTile}
                  backActionLabel={translations.backActionLabel}
                  savingStatus={showEditActions ? savingStatus : undefined}
                  presence={<Presence />}
                  reviewButton={
                    showEditActions ? (
                      <ReviewChecklistItem
                        dossierId={dossierId || ""}
                        checklistItemId={checklistItemId || ""}
                        disabled={
                          formEditableStatus !== ChecklistItemStatusType.Open
                        }
                      />
                    ) : undefined
                  }
                >
                  <RootScreen renewHint={renewHintRef.current} />
                </BackActionLayout>
              )}
            </>
          </PresenceProvider>
        )}
      </InitialDataLayer>
    </IntersectionObserverInjector>
  );
};

export { QuestionnaireModule };
